/**
 * Toolbar — top-left tool cluster + contextual hint line.
 * Select / sketch entity tools (line, rect, circle, arc) / finish-cancel
 * sketch / extrude / undo / redo. Re-renders from App state.
 */

import type { App } from '../app';
import type { ExportFormat } from '@workers/ioClient';
import { icons } from './icons';

/** Auto-generated parameter form for a published custom tool. */
function showCustomToolForm(
  anchor: HTMLElement,
  tool: { name: string; parameters: { properties: Record<string, unknown> } },
  onRun: (args: Record<string, unknown>) => void,
): void {
  document.querySelector('.nv-menu')?.remove();
  const menu = document.createElement('div');
  menu.className = 'nv-menu';
  const rect = anchor.getBoundingClientRect();
  menu.style.left = `${Math.min(rect.left, window.innerWidth - 220)}px`;
  menu.style.top = `${rect.bottom + 6}px`;
  menu.style.padding = '8px';

  const props = Object.entries(tool.parameters?.properties ?? {});
  const inputs = new Map<string, HTMLInputElement>();
  for (const [key, schema] of props) {
    const s = schema as { type?: string; description?: string };
    const label = document.createElement('label');
    label.textContent = `${key}${s.description ? ` — ${s.description}` : ''}`;
    label.style.cssText = 'font-size:10.5px;color:#8b93a1;display:block;margin:4px 0 2px;';
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = s.type ?? 'value';
    input.style.cssText =
      'width:100%;box-sizing:border-box;padding:4px 6px;border-radius:5px;' +
      'border:1px solid #2c313a;background:#14171c;color:#d7dae0;font:inherit;';
    inputs.set(key, input);
    menu.append(label, input);
  }
  const run = document.createElement('button');
  run.textContent = props.length ? `Run ${tool.name}` : `Run ${tool.name} (no parameters)`;
  run.style.marginTop = '8px';
  run.addEventListener('click', () => {
    const args: Record<string, unknown> = {};
    for (const [key, input] of inputs) {
      const v = input.value.trim();
      if (!v) continue;
      const t = (tool.parameters.properties[key] as { type?: string })?.type;
      if (t === 'number') args[key] = Number(v);
      else if (t === 'boolean') args[key] = v === 'true' || v === '1';
      else if (t === 'array' || t === 'object') {
        try {
          args[key] = JSON.parse(v);
        } catch {
          args[key] = v;
        }
      } else args[key] = v;
    }
    menu.remove();
    onRun(args);
  });
  menu.appendChild(run);
  const dismiss = (e: PointerEvent): void => {
    if (e.target instanceof Node && menu.contains(e.target)) return;
    menu.remove();
    window.removeEventListener('pointerdown', dismiss, true);
  };
  window.addEventListener('pointerdown', dismiss, true);
  document.body.appendChild(menu);
}

/** Small anchored popup for picking the export format. */
function showExportMenu(
  anchor: HTMLElement,
  onPick: (f: ExportFormat | 'step') => void,
): void {
  document.querySelector('.nv-menu')?.remove();
  const menu = document.createElement('div');
  menu.className = 'nv-menu';
  const rect = anchor.getBoundingClientRect();
  menu.style.left = `${rect.left}px`;
  menu.style.top = `${rect.bottom + 6}px`;
  for (const [format, label] of [
    ['step', 'STEP — exact B-rep solids'],
    ['stl', 'STL — 3D printing'],
    ['obj', 'OBJ — universal mesh'],
    ['glb', 'GLB — glTF binary'],
  ] as const) {
    const item = document.createElement('button');
    item.textContent = label;
    item.addEventListener('click', () => {
      menu.remove();
      onPick(format);
    });
    menu.appendChild(item);
  }
  const dismiss = (e: PointerEvent): void => {
    if (e.target instanceof Node && menu.contains(e.target)) return;
    menu.remove();
    window.removeEventListener('pointerdown', dismiss, true);
  };
  window.addEventListener('pointerdown', dismiss, true);
  document.body.appendChild(menu);
}

export function mountToolbar(host: HTMLElement, app: App): void {
  const bar = document.createElement('div');
  bar.className = 'nv-toolbar';
  const hint = document.createElement('div');
  hint.className = 'nv-hint';
  const ioStatus = document.createElement('div');
  ioStatus.className = 'nv-iostatus';
  ioStatus.style.display = 'none';
  host.append(bar, hint, ioStatus);

  // Hidden file input for mesh import.
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = '.stl,.obj,.step,.stp,.iges,.igs';
  fileInput.multiple = true;
  fileInput.style.display = 'none';
  fileInput.addEventListener('change', () => {
    if (fileInput.files?.length) void app.importMeshFiles([...fileInput.files]);
    fileInput.value = '';
  });
  host.appendChild(fileInput);

  const render = (): void => {
    const sk = app.sketch;
    bar.innerHTML = '';

    const btn = (
      icon: string,
      title: string,
      onClick: (e?: MouseEvent) => void,
      opts: { active?: boolean; disabled?: boolean } = {},
    ): void => {
      const b = document.createElement('button');
      b.className = `nv-btn${opts.active ? ' active' : ''}`;
      b.title = title;
      b.innerHTML = icon;
      b.disabled = !!opts.disabled;
      b.addEventListener('pointerdown', (e) => e.stopPropagation());
      b.addEventListener('click', onClick);
      bar.appendChild(b);
    };
    const sep = (): void => {
      const s = document.createElement('div');
      s.className = 'sep';
      bar.appendChild(s);
    };

    btn(
      icons.select,
      'Select (Esc) — click to select, drag a box (never edits geometry)',
      () => {
        if (sk.active) void app.finishSketch();
        else if (app.pushPullMode) app.togglePushPull();
        else if (app.orbitMode) app.toggleOrbit();
        else app.select(null);
      },
      { active: !sk.active && !app.pushPullMode && !app.orbitMode },
    );
    sep();

    for (const [kind, icon, title] of [
      ['line', icons.line, 'Line (L)'],
      ['rect', icons.rect, 'Rectangle (R)'],
      ['circle', icons.circle, 'Circle (C)'],
      ['ellipse', icons.ellipse, 'Ellipse (E) — drag center→corner; type "rx,ry"'],
      ['arc2', icons.arc2, '2-Point Arc (A) — ends, then bulge'],
      ['arc', icons.arc, 'Center Arc — center, start, end'],
      ['spline', icons.spline, 'Spline (S) — fair curve through clicked points'],
      ['text', icons.text, 'Text (T) — click a point, type the string (extrude or engrave it)'],
      ['polygon', icons.polygon, 'Polygon (P) — drag center→vertex; type "radius,sides"'],
      ['slot', icons.slot, 'Slot (O) — click both centerline ends, then the width'],
    ] as const) {
      btn(icon, title, () => app.setSketchEntity(kind), {
        active: sk.active && sk.entityKind === kind && !sk.trimMode,
      });
    }

    if (sk.active) {
      sep();
      btn(icons.trim, 'Trim (X) — click the piece of an entity to remove (cuts at crossings)', () =>
        sk.toggleTrim(), { active: sk.trimMode });
      btn(icons.check, 'Finish sketch (Enter)', () => void app.finishSketch());
      btn(icons.x, 'Cancel sketch', () => app.cancelSketch());
    } else {
      sep();
      btn(
        icons.orbit,
        'Orbit (O) — drag to rotate the view. Off = left-drag is the select box (⌥drag/middle always orbit)',
        () => app.toggleOrbit(),
        { active: app.orbitMode },
      );
      btn(
        icons.pushpull,
        'Push/Pull (P) — drag an extrude cap to change its depth (⌥ = taper). Off = Select never edits geometry',
        () => app.togglePushPull(),
        { active: app.pushPullMode },
      );
      btn(icons.extrude, 'Extrude selected sketch', () => void app.extrudeSelected(), {
        disabled: !app.canExtrude,
      });
      btn(icons.revolve, 'Revolve selected sketch', () => void app.revolveSelected(), {
        disabled: !app.canExtrude,
      });
      btn(icons.sweep, 'Sweep: profile sketch along a path sketch', () => app.startSweep(), {
        disabled: !app.canExtrude,
        active: app.pendingSweepProfile !== null,
      });
      btn(
        icons.loft,
        'Loft through selected sketches (⇧click 2+ station sketches in order)',
        () => void app.loftSelected(),
        { disabled: !app.canLoft },
      );
      btn(icons.scale, 'Scale selected body', () => void app.scaleSelected(), {
        disabled: !app.canScale,
      });
      btn(icons.mirror, 'Mirror selected body', () => void app.mirrorSelected(), {
        disabled: !app.canScale,
      });
      btn(
        icons.move,
        'Move/Rotate selected body (⌥ = copy, then type 5x to array)',
        () => void app.moveSelected(),
        { disabled: !app.canScale },
      );
      btn(icons.fillet, 'Fillet (round) edges — click edges to pick, Enter applies (no picks = all)', () =>
        app.edgePick?.kind === 'fillet'
          ? app.cancelEdgePick()
          : app.startEdgePick('fillet', app.selected!),
        { disabled: !app.canDressup && !app.edgePick, active: app.edgePick?.kind === 'fillet' });
      btn(icons.chamfer, 'Chamfer (bevel) edges — click edges to pick, Enter applies (no picks = all)', () =>
        app.edgePick?.kind === 'chamfer'
          ? app.cancelEdgePick()
          : app.startEdgePick('chamfer', app.selected!),
        { disabled: !app.canDressup && !app.edgePick, active: app.edgePick?.kind === 'chamfer' });
      btn(icons.hole, 'Hole — click positions on a face, Enter drills (M-size in Properties)', () =>
        app.holePick ? app.cancelHolePick() : app.startHolePick(app.selected!),
        { disabled: !app.canDressup && !app.holePick, active: !!app.holePick });
      btn(icons.pattern, 'Pattern selected body (linear/circular, parametric count)', () =>
        void app.patternSelected(), { disabled: !app.canScale });
      btn(icons.coil, 'Coil — helical spring/thread (radius/pitch/turns in Properties)', () =>
        void app.addCoil());
      btn(icons.group, 'Group selected bodies (shift-click to multi-select)', () =>
        void app.groupSelected(), { disabled: !app.canGroup });
      btn(icons.instance, 'Component instance: placed copy that tracks its source', () =>
        void app.instanceSelected(), { disabled: !app.canScale });
    }

    sep();
    btn(icons.measure, 'Measure distance (M) — click two points', () => app.toggleMeasure(), {
      active: app.measure.active,
    });
    btn(icons.section, 'Section view — slice the model at a plane', () => app.toggleSectionPanel(), {
      active: app.sectionOpen,
    });
    btn(
      icons.curvature,
      'Curvature display — color surfaces by curvature (fairing check)',
      () => app.toggleCurvature(),
      { active: app.viewport.curvatureOn },
    );
    btn(icons.fx, 'Parameters (named values, expressions)', () => app.togglePanel('params'), {
      active: app.activePanel === 'params',
    });
    sep();
    btn(icons.undo, 'Undo (⌘Z)', () => void app.doc.undo(), { disabled: !app.doc.canUndo });
    btn(icons.redo, 'Redo (⌘⇧Z)', () => void app.doc.redo(), { disabled: !app.doc.canRedo });
    sep();
    btn(icons.import, 'Import (STL/OBJ/STEP/IGES)', () => fileInput.click());
    btn(icons.drawing, 'Drawing sheet — front/top/right/iso + dims, DXF/PDF', () =>
      app.toggleDrawing(), { active: app.drawingOpen });
    btn(icons.export, 'Export… (STEP/STL/OBJ/GLB)', (e) => {
      const anchor = (e?.currentTarget as HTMLElement) ?? bar;
      showExportMenu(anchor, (format) =>
        format === 'step' ? void app.exportStepFile() : void app.exportAs(format),
      );
    });
    btn(icons.ai, 'AI Render', () => app.togglePanel('ai'), { active: app.activePanel === 'ai' });
    btn(icons.library, 'Asset library (Poly Haven)', () => app.togglePanel('assets'), {
      active: app.activePanel === 'assets',
    });
    btn(icons.shell, 'Bodies', () => app.togglePanel('bodies'), {
      active: app.activePanel === 'bodies',
    });
    btn(icons.folder, `Projects (current: ${app.doc.name})`, () => app.togglePanel('projects'), {
      active: app.activePanel === 'projects',
    });
    btn(icons.chat, 'Assistant (LLM copilot)', () => app.togglePanel('assistant'), {
      active: app.activePanel === 'assistant',
    });

    // Published custom tools (defined by the assistant, promoted to the GUI).
    const published = app.toolbox.customTools.filter((t) => t.published);
    if (published.length > 0) sep();
    for (const tool of published) {
      btn(icons.wrench, `${tool.name} — ${tool.description}`, (e) => {
        const anchor = (e?.currentTarget as HTMLElement) ?? bar;
        showCustomToolForm(anchor, tool, async (args) => {
          try {
            const result = await app.toolbox.run(tool.name, args);
            app.setIoStatus(
              `${tool.name}: ${JSON.stringify(result ?? 'done').slice(0, 120)}`,
            );
          } catch (err) {
            app.setIoStatus(`${tool.name} failed: ${err instanceof Error ? err.message : err}`);
          }
        });
      });
    }

    ioStatus.textContent = app.ioStatus;
    ioStatus.style.display = app.ioStatus ? 'block' : 'none';

    const text = app.orbitMode
      ? 'Orbit: drag to rotate the view · O or Esc exits (back to select box)'
      : app.pushPullMode
      ? 'Push/Pull: drag an extrude cap face to change its depth · ⌥drag = taper · P or Esc exits'
      : app.edgePick
      ? `${app.edgePick.kind === 'fillet' ? 'Fillet' : 'Chamfer'}: click edges to pick/unpick (${app.edgePick.picked.length} picked) · Enter applies · no picks = ALL edges · Esc cancels`
      : app.holePick
      ? `Hole: click positions on the body's faces (${app.holePick.picked.length} picked) · Enter drills · Esc cancels`
      : app.matePick
      ? `Concentric mate: click a cylindrical face on ${app.doc.timeline.get(app.matePick.faceTarget ? app.matePick.fixed : app.matePick.moving)?.name} · Esc cancels`
      : app.pendingSweepProfile
      ? 'Sweep armed — click the PATH sketch (timeline chip with an open curve) · Esc cancels'
      : app.measure.active
        ? app.measure.hint
        : sk.active
          ? sk.hint
        : app.selection.length > 1
          ? `${app.selection.length} bodies selected — Group bundles them into one`
          : app.canExtrude
            ? 'Sketch selected — Extrude, Revolve, or Sweep it, or draw more'
            : 'Click: select · drag →: box whole · drag ←: box touching · ⇧ adds · O: orbit · P: push/pull';
    hint.textContent = text;
    hint.style.display = text ? 'block' : 'none';
  };

  app.onUI(render);
  render();
}
